import React, { useMemo, useState } from "react";
import { Button, FeaturedIcon, Modal } from "@myhealth-raccoon/components";
import { Navigate, useNavigate } from "react-router-dom";

import { ContentContainer } from "src/components/ContentContainer";
import { StickyBottomBar } from "src/components/StickyBottomBar";
import { innerHtml } from "src/utils/html";
import { setSawPrepaymentMessage } from "src/store/bookingFlow";
import { useDispatch, useSelector } from "src/store";
import { useAppointmentTypeSelector } from "src/hooks/useAsyncSelector";
import { PleaseWait } from "src/components/PleaseWait";
import { PreventBackButton } from "src/components/functional";
import { AlertCircle } from "@untitled-ui/icons-react";
import { useExit } from "src/hooks/useExit";
import { useBeforeUnload } from "src/hooks/useBeforeUnload";

export function Prepayment({ nextPage, skipPayment, reset }: { nextPage: string, skipPayment: string, reset: string }) {
    const [showNegativeMessage, setShowNegativeMessage] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const paymentStatus = useSelector(state => state.bookingFlow.paymentStatus);
    const { data: appointmentType } = useAppointmentTypeSelector();
    const exit = useExit();
    useBeforeUnload();

    const { prepaymentHtml, paymentRequired } = useMemo(() => {
        if (!appointmentType) {
            return { prepaymentHtml: undefined, paymentRequired: undefined };
        }
        if (appointmentType.prePaymentRequired && Number(appointmentType.prePaymentFees) > 0) {
            return { prepaymentHtml: appointmentType.prePaymentMessageInHTML, paymentRequired: true };
        } else if (appointmentType.postPaymentRequired && Number(appointmentType.postBillingFees) > 0) {
            return { prepaymentHtml: appointmentType.postBillingMessageInHTML, paymentRequired: true };
        } else {
            return { prepaymentHtml: undefined, paymentRequired: false };
        }
    }, [appointmentType]);

    if (paymentRequired === false || paymentStatus === "COMPLETED") {
        return <Navigate to={skipPayment} replace />
    }

    if (paymentStatus === 'STARTED') {
        // need to throw out the appointment
        dispatch(setSawPrepaymentMessage(false));
        return <Navigate to={reset} replace />
    }

    if (paymentRequired === undefined) {
        return <ContentContainer><PleaseWait /></ContentContainer>
    }

    return <>
        <ContentContainer className="text-center flex-1 lg:flex-none">
            <FeaturedIcon className='mx-auto mb-2.5' />
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Please read the following</h1>
            {prepaymentHtml && <div className='prose-sm' {...innerHtml(prepaymentHtml)} />}
        </ContentContainer>
        <StickyBottomBar>
            <Button
                className="flex-1 text-center flex items-center justify-center gap-2"
                color="gray"
                onClick={() => setShowNegativeMessage(true)}
            >No</Button>
            <Button
                className='flex-1 text-center flex items-center justify-center gap-2'
                onClick={() => {
                    navigate(nextPage)
                }}
            >
                Yes
            </Button>
        </StickyBottomBar>
        <Modal
            icon={{ icon: AlertCircle, color: 'error' }}
            open={showNegativeMessage}
            title="We are currently unable to schedule an appointment for you"
            onClose={() => setShowNegativeMessage(false)}
        >
            Please contact the clinic directly or seek care from alternative healthcare doctors in your area.
            <div className="flex flex-row gap-4 mt-4">
                <Button
                    className="flex-1 text-center flex items-center justify-center gap-2"
                    color="gray"
                    onClick={() => exit()}>
                    Exit
                </Button>
                <Button
                    className='flex-1 text-center flex items-center justify-center gap-2'
                    onClick={() => setShowNegativeMessage(false)}
                >
                    Cancel
                </Button>

            </div>
        </Modal>
        <PreventBackButton />
    </>
}